import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Repeat, Search, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import ClientDialog from "@/components/clients/ClientDialog";
import ClientDetail from "@/components/clients/ClientDetail";
import ImportClientsDialog from "@/components/clients/ImportClientsDialog";
import { useAuth } from "@/context/AuthContext";
import { CLIENT_TYPES, clientTypeLabel } from "@/lib/services";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function ClientList({ clients, selectedId, onSelect }) {
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const shown = clients.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) && (type === "all" || c.client_type === type));
  return (
    <section className="rounded-2xl border border-[#103080]/10 bg-white/70 ambient p-4" data-testid="client-list">
      <div className="relative mb-3">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#103080]/40" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients"
          className="pl-9 rounded-full bg-[#FDFBF7] border-[#103080]/15 h-10" data-testid="client-search-input" />
      </div>
      <Select value={type} onValueChange={setType}>
        <SelectTrigger className="mb-3 rounded-full bg-[#FDFBF7] border-[#103080]/15 h-10" data-testid="client-type-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All client types</SelectItem>
          {CLIENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {shown.length === 0 ? (
        <p className="text-sm text-[#0F172A]/50 text-center py-8" data-testid="client-list-empty">No clients yet.</p>
      ) : (
        <ul className="space-y-1 max-h-[640px] overflow-y-auto thin-scroll">
          {shown.map((c) => (
            <li key={c.id}>
              <button onClick={() => onSelect(c.id)} data-testid={`client-item-${c.id}`}
                className={`w-full text-left rounded-xl px-3 py-3 flex items-center justify-between gap-3 transition-colors duration-200 ${selectedId === c.id ? "bg-[#103080] text-white" : "hover:bg-[#F5F2EB] text-[#103080]"}`}>
                <span className="min-w-0">
                  <span className="block font-semibold text-sm truncate">{c.name}</span>
                  {clientTypeLabel(c) && <span className={`block text-[11px] truncate ${selectedId === c.id ? "text-white/70" : "text-[#0F172A]/50"}`}>{clientTypeLabel(c)}</span>}
                </span>
                <span className={`text-xs inline-flex items-center gap-1 shrink-0 ${selectedId === c.id ? "text-white/70" : "text-[#0F172A]/50"}`}>
                  <Repeat size={11} />{c.job_count}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function Clients() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [dialog, setDialog] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/clients");
      setClients(data);
      setSelectedId((cur) => cur || data[0]?.id || "");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selected = clients.find((c) => c.id === selectedId);

  return (
    <div className="space-y-8 fade-up" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-semibold mb-3">Clients & recurring work</p>
          <h1 className="font-serif-brand font-semibold text-[#103080] text-4xl sm:text-5xl">Work that <i className="text-[#0090D0]">repeats itself</i></h1>
          <p className="mt-3 text-[#0F172A]/60 max-w-xl">Set up VAT quarters, payroll runs and year-ends once. Each one appears in the right person's planner ahead of its deadline.</p>
        </div>
        {user.role === "admin" && <div className="flex gap-2 shrink-0">
          <button onClick={() => setImporting(true)} data-testid="import-clients-button"
            className="inline-flex items-center gap-2 rounded-full border border-[#103080]/20 bg-white/70 text-[#103080] font-medium px-5 h-11 text-sm hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-200">
            <Upload size={15} /> Import clients
          </button>
          <button onClick={() => setDialog({})} data-testid="add-client-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#103080] text-white font-medium px-6 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200">
            <Plus size={16} /> Add client
          </button>
        </div>}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 xl:col-span-3">
          <ClientList clients={clients} selectedId={selectedId} onSelect={setSelectedId} />
        </div>
        <div className="lg:col-span-8 xl:col-span-9 min-w-0">
          {selected ? (
            <ClientDetail client={selected} onEdit={() => setDialog(selected)} onChanged={load}
              onDeleted={() => { setSelectedId(""); load(); }} />
          ) : (
            <div className="rounded-2xl border border-dashed border-[#103080]/20 p-12 text-center text-[#0F172A]/50" data-testid="client-detail-empty">
              Add your first client to start setting up recurring jobs.
            </div>
          )}
        </div>
      </div>
      <ClientDialog client={dialog} onClose={() => setDialog(null)} onSaved={(c) => { setSelectedId(c.id); load(); }} />
      <ImportClientsDialog open={importing} onClose={() => setImporting(false)} onDone={load} />
    </div>
  );
}
